import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import SEO from './SEO';
import emailjs from '@emailjs/browser';
import SnakeGame from './SnakeGame';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

export default function ContactPage() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const form = useRef();
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSending(true);
    setErrorMessage('');

    // REPLACE THESE WITH YOUR ACTUAL EMAILJS KEYS
    // Sign up at https://www.emailjs.com/
    const SERVICE_ID = 'service_kw0sfgn';
    const TEMPLATE_ID = 'template_sjj30cf';
    const PUBLIC_KEY = 'EG7_eXs8aW13pETh7';

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, form.current, PUBLIC_KEY)
      .then((result) => {
        console.log(result.text);
        setIsSending(false);
        setIsSent(true);
        setFormState({ name: '', email: '', message: '' });
        setTimeout(() => setIsSent(false), 3000);
      }, (error) => {
        console.log(error.text);
        setIsSending(false);
        setErrorMessage('Error al enviar el mensaje. Por favor, verifica tu conexión o inténtalo más tarde.');
        setTimeout(() => setErrorMessage(''), 5000);
      });
  };

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const inputClasses = `w-full px-4 py-3 rounded-none border-b-2 focus:outline-none transition-colors duration-300 ${theme === 'dark'
    ? 'bg-transparent border-gray-700 text-white focus:border-white placeholder-gray-500'
    : 'bg-transparent border-gray-300 text-charcoal focus:border-charcoal placeholder-gray-400'
    }`;

  const labelClasses = `block text-sm font-bold mb-2 uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
    }`;

  return (
    <div className={`min-h-screen pt-24 pb-12 ${theme === 'dark' ? 'bg-dark-bg' : 'bg-off-white'}`}>
      <SEO
        title={t('contact.title')}
        description={t('contact.subtitle')}
        canonicalPath="/contacto"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className={`text-5xl md:text-7xl font-bold mb-4 tracking-tighter ${theme === 'dark' ? 'text-white' : 'text-charcoal'}`}>
            {t('contact.title')}
          </h1>
          <p className={`text-xl font-light ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <form ref={form} onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label htmlFor="name" className={labelClasses}>
                  {t('contact.name')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                />
              </div>

              <div>
                <label htmlFor="email" className={labelClasses}>
                  {t('contact.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                />
              </div>

              <div>
                <label htmlFor="message" className={labelClasses}>
                  {t('contact.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className={inputClasses}
                />
              </div>

              <button
                type="submit"
                disabled={isSending}
                className={`w-full py-4 px-8 font-bold text-lg tracking-widest uppercase transition-all duration-300 ${theme === 'dark'
                  ? 'bg-white text-charcoal hover:bg-gray-200'
                  : 'bg-charcoal text-white hover:bg-black'
                  } ${isSending ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSending ? t('contact.sending') : isSent ? t('contact.sent') : t('contact.send')}
              </button>
              {errorMessage && (
                <p className="text-red-500 text-center mt-4 font-bold">
                  {errorMessage}
                </p>
              )}
            </form>
          </motion.div>

          {/* Minigame Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="hidden lg:flex flex-col items-center justify-center"
          >
            <div className="w-full max-w-md">
              <SnakeGame />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}